import { forwardRef, type InputHTMLAttributes } from "react";

type Props = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string | undefined;
  // Renders a red asterisk after the label and forwards required to the
  // input element for browser-side semantics. Visual marker is purely
  // additive — actual validation still flows through zod / react-hook-form.
  required?: boolean;
};

export const Field = forwardRef<HTMLInputElement, Props>(function Field(
  { label, error, id, required, ...rest },
  ref,
) {
  const inputId = id ?? `field-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const errorId = error ? `${inputId}-err` : undefined;
  return (
    <div>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-slate-700"
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        ) : null}
      </label>
      <input
        id={inputId}
        ref={ref}
        required={required}
        aria-required={required ? "true" : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={errorId}
        className={
          "field-input mt-1 block w-full rounded-md border bg-white px-3 py-2 " +
          "text-sm text-slate-900 placeholder:text-slate-400 shadow-sm " +
          "hover:border-slate-400 " +
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 " +
          (error ? "border-red-400" : "border-slate-300")
        }
        {...rest}
      />
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
});
